// #/modules/slots.ts
import type React from 'react'

export type Renderer = React.ElementType

type SlotContrib = {slotId: string; renderer: Renderer}

type SlotsSingleton = {
  slots: Map<string, Renderer[]>
  contribs: Map<string, SlotContrib[]>
  subs: Set<() => void>
  ver: number
  bump: () => void
}

declare global {
  // eslint-disable-next-line no-var
  var __HORIZON_SLOTS_SINGLETON__: SlotsSingleton | undefined
}

function getSingleton(): SlotsSingleton {
  const existing = globalThis.__HORIZON_SLOTS_SINGLETON__
  if (existing) return existing

  // Create once, store on globalThis so even if bundler loads this module twice,
  // all copies share the same backing store.
  const singleton: SlotsSingleton = {
    slots: new Map(),
    contribs: new Map(),
    subs: new Set(),
    ver: 0,
    bump: () => {
      singleton.ver++

      // Snapshot to avoid issues if subs mutate during iteration
      for (const fn of Array.from(singleton.subs)) {
        try {
          fn()
        } catch (e) {
          // Silent error - subscriber threw
        }
      }
    },
  }

  globalThis.__HORIZON_SLOTS_SINGLETON__ = singleton
  return singleton
}

const S = getSingleton()

export function subscribeSlots(fn: () => void) {
  S.subs.add(fn)

  // Important: fire once on next microtask so screens mounting "late"
  // (or after a reload-on-subpage) immediately reconcile with current slot state.
  queueMicrotask(() => {
    if (!S.subs.has(fn)) return
    try {
      fn()
    } catch {}
  })

  return () => {
    S.subs.delete(fn)
  }
}

export function registerSlot(moduleId: string, slotId: string, renderer: Renderer) {
  // Track per-module contributions so we can unload cleanly
  const list = S.contribs.get(moduleId) ?? []
  list.push({slotId, renderer})
  S.contribs.set(moduleId, list)

  // Register into the slot renderer list (de-dupe to avoid stacking on resync/reload)
  const arr = S.slots.get(slotId) ?? []
  if (!arr.includes(renderer)) {
    arr.push(renderer)
    S.slots.set(slotId, arr)
  } else {
    // Ensure map entry exists even if it was missing (paranoia)
    S.slots.set(slotId, arr)
  }

  // eslint-disable-next-line no-console
  console.log('[slots] register', {
    moduleId,
    slotId,
    count: arr.length,
    v: S.ver + 1,
  })
  S.bump()
}

export function unregisterModuleSlots(moduleId: string) {
  const list = S.contribs.get(moduleId)
  if (!list || list.length === 0) {
    // eslint-disable-next-line no-console
    console.log('[slots] unregisterModuleSlots: none', moduleId)
    return
  }

  for (const {slotId, renderer} of list) {
    const arr = S.slots.get(slotId) ?? []
    const next = arr.filter(r => r !== renderer)

    if (next.length === 0) S.slots.delete(slotId)
    else S.slots.set(slotId, next)
  }

  S.contribs.delete(moduleId)
  S.bump()
}

export function getSlotRenderers(slotId: string): Renderer[] {
  const arr = S.slots.get(slotId) ?? []
  return arr
}

export function getSlotsVersion(): number {
  return S.ver
}
